import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Field } from 'redux-form/immutable';
import { ListItem, ListItemText } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@material-ui/core/FormControl';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import { selectUser } from 'entities/user/selectors';
import FormTextField from 'retired/FormTextField';
import FormFileUpload from 'retired/FormFileUpload';
import FormFieldLabel from 'retired/FormFieldLabel';
import SelectableChip from 'retired/SelectableChip';
import CheckCircleIcon from 'assets/icons/check-circle.svg';
import Img from 'retired/Img';
import * as validators from 'utils/validators';
import { computeDonationProviderForNonprofit } from 'utils/computeDonationProviderForNonprofit';
import { getDonationProviderDisplayName } from 'utils/getDonationProviderDisplayName';
import NonprofitLink from 'retired/NonprofitLink';

const required = validators.required();

const SelectedOption = React.memo(({ fieldName, nonprofit, remove, isEditable }) => {
  const [isEditing, setIsEditing] = useState(false);
  const user = useSelector(selectUser);
  const preferStripe = user?.organization?.settings?.get('preferStripe') || false;

  const toggleIsEditing = () => setIsEditing(!isEditing);
  const locationObject = nonprofit.get('locationObject');
  const location = locationObject?.get('city')
    ? [
        locationObject.get('address'),
        locationObject.get('city'),
        locationObject.get('state') &&
          locationObject.get('postalCode') &&
          `${locationObject.get('state')} ${locationObject.get('postalCode')}`,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const providerCode = computeDonationProviderForNonprofit(nonprofit, preferStripe);
  const providerName = getDonationProviderDisplayName(providerCode);
  const showPoweredBy = user?.isAdmin || false;

  return (
    <ListItem
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        border: isEditing ? '1px solid rgba(0, 0, 0, 0.2)' : '1px',
        borderRadius: 16,
        padding: 15,
        width: '100%',
        cursor: 'move',
        pointerEvents: 'none',
        paddingTop: 20,
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '100%',
          textAlign: 'center',
          top: 8,
        }}
      >
        <DragHandleIcon />
      </div>
      {isEditing ? (
        <div>
          <FormControl style={{ flexGrow: 1, width: '100%' }}>
            <Field
              name={`${fieldName}.name`}
              component={FormTextField}
              placeholder="Nonprofit name"
              validate={required}
              required
            />
          </FormControl>
          <FormControl style={{ flexGrow: 1, width: '100%', marginTop: 15 }}>
            <Field
              name={`${fieldName}.brief`}
              component={FormTextField}
              placeholder="Nonprofit brief"
              style={{ fontSize: 12 }}
            />
          </FormControl>
          <FormControl style={{ flexGrow: 1, width: '100%', marginTop: 15 }}>
            <FormFieldLabel>Logo</FormFieldLabel>
            <Field
              name={`${fieldName}.mainPicture`}
              component={FormFileUpload}
              labelText="Make sure that the image is transparent and that the image format is a PNG."
              acceptedTypes="image/png"
              maxSize={1024 * 1024 * 10}
              compact
            />
          </FormControl>
        </div>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={remove}
            title="Remove nonprofit"
            style={{ pointerEvents: 'initial' }}
          >
            <ClearIcon size="small" />
          </IconButton>
          <ListItemText
            style={{ flex: 1 }}
            primary={
              <>
                <NonprofitLink nonprofit={nonprofit.toJS()} />{' '}
                {nonprofit.get('ein') ? <>– EIN: {nonprofit.get('ein')}</> : null}
                {nonprofit.get('approved') && (
                  <Img
                    src={CheckCircleIcon}
                    style={{ width: '20px', marginLeft: 6 }}
                    alt="Approved"
                    title="Approved"
                  />
                )}
              </>
            }
            secondary={
              <>
                {location}
                {showPoweredBy && providerName && (
                  <>
                    <br />
                    Powered by: {providerName}
                  </>
                )}
              </>
            }
          />
        </>
      )}
      {isEditable && nonprofit.get('externalId') && (
        <div style={{ marginLeft: 20 }}>
          <SelectableChip
            onClick={toggleIsEditing}
            label={isEditing ? 'Save' : 'Edit'}
            style={{ pointerEvents: 'initial' }}
          />
        </div>
      )}
    </ListItem>
  );
});
SelectedOption.propTypes = {
  remove: PropTypes.func,
  nonprofit: PropTypes.object,
  fieldName: PropTypes.string,
  isEditable: PropTypes.bool,
};

export default SelectedOption;
